import { useEffect, useState } from "react";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import config from "config";
import { getAuthToken } from "lib/providers";

export interface LinkPreviewData {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
}

/** Status of the OpenGraph fetch — drives whether to render a skeleton. */
export type LinkPreviewStatus =
  | "idle"        // no URL in text
  | "loading"     // URL detected, fetch in flight
  | "loaded"      // fetch resolved with usable preview data
  | "no-preview"; // fetch resolved (or cache says) there's no usable preview

export interface UseLinkPreviewResult {
  preview: LinkPreviewData | null;
  status: LinkPreviewStatus;
}

const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/i;

// In-memory cache for the current page session. Hot path: re-renders of
// the same bubble while scrolling.
const previewCache = new Map<string, LinkPreviewData | null>();

// localStorage-backed cache persists across page reloads / chat re-opens.
// Stores both successful previews AND "no-preview" verdicts (with a shorter
// TTL for negatives, so OG metadata that lands later still gets re-tried).
const PERSIST_KEY_PREFIX = "linkPreview:v1:";
const PERSIST_TTL_OK_MS = 7 * 24 * 60 * 60 * 1000;     // 7 days for hits
const PERSIST_TTL_MISS_MS = 24 * 60 * 60 * 1000;       // 1 day for misses
const PERSIST_AVAILABLE = (() => {
  // Some browsers / Capacitor edge cases throw on localStorage access
  // (private mode quota, security restrictions). Probe once and fall back
  // to in-memory only if it fails.
  try {
    const probe = "__linkPreview_probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

interface PersistedEntry {
  data: LinkPreviewData | null;
  ts: number; // ms epoch
}

function readPersisted(url: string): PersistedEntry | null {
  if (!PERSIST_AVAILABLE) return null;
  try {
    const raw = localStorage.getItem(PERSIST_KEY_PREFIX + url);
    if (!raw) return null;
    const entry: PersistedEntry = JSON.parse(raw);
    const ttl = entry.data ? PERSIST_TTL_OK_MS : PERSIST_TTL_MISS_MS;
    if (Date.now() - entry.ts > ttl) {
      localStorage.removeItem(PERSIST_KEY_PREFIX + url);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

function writePersisted(url: string, data: LinkPreviewData | null): void {
  if (!PERSIST_AVAILABLE) return;
  try {
    localStorage.setItem(
      PERSIST_KEY_PREFIX + url,
      JSON.stringify({ data, ts: Date.now() }),
    );
  } catch {
    // Quota exceeded or other write failure: drop silently.
  }
}

export function extractFirstUrl(text: string): string | null {
  const match = text.match(URL_REGEX);
  return match ? match[0] : null;
}

async function fetchPreview(endpoint: string, headers: Record<string, string>): Promise<LinkPreviewData> {
  if (Capacitor.isNativePlatform()) {
    const resp = await CapacitorHttp.get({ url: endpoint, headers });
    if (resp.status < 200 || resp.status >= 300) throw new Error(`HTTP ${resp.status}`);
    return typeof resp.data === "string" ? JSON.parse(resp.data) : resp.data;
  }
  const res = await fetch(endpoint, { headers });
  if (!res.ok) throw new Error("Failed");
  return res.json();
}

/**
 * Hook that turns a chat message text into an OpenGraph preview if it
 * contains a fetchable URL. Returns both the resolved preview data AND a
 * status flag so callers can render a skeleton slot during the fetch —
 * preventing the 200–260 px layout jump that would otherwise yank the
 * chat viewport when the preview card materialises post-mount.
 *
 * Two-tier cache:
 *   - Hot: in-memory `Map` keyed by URL (per-session, instant on re-render).
 *   - Warm: `localStorage` (per-origin) with 7-day TTL for hits, 1-day TTL
 *     for misses. Survives page reloads / chat re-opens.
 */
export default function useLinkPreview(text?: string): UseLinkPreviewResult {
  const [preview, setPreview] = useState<LinkPreviewData | null>(null);
  const [status, setStatus] = useState<LinkPreviewStatus>("idle");

  const url = text ? extractFirstUrl(text) : null;

  useEffect(() => {
    if (!url) {
      setPreview(null);
      setStatus("idle");
      return;
    }

    // Direct-media URLs (image/video) are rendered inline elsewhere — don't
    // generate a preview card for them.
    if (/\.(gif|jpg|jpeg|png|webp|svg|bmp|avif|mp4|webm|mov)(\?.*)?$/i.test(url)) {
      setPreview(null);
      setStatus("no-preview");
      return;
    }

    // Hot in-memory cache: synchronous hit, no loading flicker.
    if (previewCache.has(url)) {
      const cached = previewCache.get(url) || null;
      setPreview(cached);
      setStatus(cached ? "loaded" : "no-preview");
      return;
    }

    // Warm localStorage cache: synchronous-ish hit (one JSON parse). Also
    // populates the hot cache so subsequent renders skip the read.
    const persisted = readPersisted(url);
    if (persisted) {
      previewCache.set(url, persisted.data);
      setPreview(persisted.data);
      setStatus(persisted.data ? "loaded" : "no-preview");
      return;
    }

    // Cache miss → kick off the OG fetch. The skeleton placeholder is
    // visible during this window.
    setPreview(null);
    setStatus("loading");

    let cancelled = false;
    const backendUrl = config.backendUrl || "/api";
    const endpoint = `${backendUrl}/link-preview?url=${encodeURIComponent(url)}`;

    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = token;
        const data = await fetchPreview(endpoint, headers);
        if (cancelled) return;
        if (data.title || data.description || data.image) {
          previewCache.set(url, data);
          writePersisted(url, data);
          setPreview(data);
          setStatus("loaded");
        } else {
          previewCache.set(url, null);
          writePersisted(url, null);
          setPreview(null);
          setStatus("no-preview");
        }
      } catch {
        if (!cancelled) {
          previewCache.set(url, null);
          writePersisted(url, null);
          setPreview(null);
          setStatus("no-preview");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { preview, status };
}
